import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { prepacksApi } from '@/services/api'

export function targetHasCanonicalPack(target) {
  return (
    (target?.status === 'valid' || target?.status === 'invalid' || target?.status === 'unavailable') &&
    target.pack &&
    Array.isArray(target.pack.legs) &&
    target.pack.legs.length > 0
  )
}

function targetsByRegulation(quote) {
  const map = new Map()
  for (const target of quote?.targets || []) {
    map.set(target.regulation, target)
  }
  return map
}

// The quote cache key, and the identity placeholderData reads back from a
// previous key.
function quoteTargetsQueryKey(slug, identity) {
  return ['prepacks', 'quote-targets', slug, identity]
}

function quoteIdentityOfKey(queryKey) {
  return queryKey[queryKey.length - 1]
}

// A leg or regulation edit of the same selection, so the previous card is a
// fair stand-in while it is checked. After clearing the legs, or for another
// source regulation, the preview waits instead.
function editsSameSelection(previousIdentity, identity) {
  try {
    const previous = JSON.parse(previousIdentity)
    const current = JSON.parse(identity)
    if (previous.source !== current.source) return false
    const outcomes = new Set(previous.legs.map((leg) => leg.outcome_id))
    return current.legs.some((leg) => outcomes.has(leg.outcome_id))
  } catch {
    return false
  }
}

// The legs to preview while a leg edit is being checked. Legs the previous
// check answered keep their checked version (per regulation); a new leg shows
// as it was found. sameLegs: only the order or the targets changed, so the
// previous price still applies.
function legsWhileChecking(answeredLegs = [], checkedLegs = [], selectedLegs) {
  const checkedByOutcome = new Map(
    answeredLegs.length === checkedLegs.length
      ? answeredLegs.map((leg, index) => [leg.outcome_id, checkedLegs[index]])
      : [],
  )
  return {
    legs: selectedLegs.map((leg) => checkedByOutcome.get(leg.outcome_id) ?? leg),
    sameLegs: selectedLegs.length === answeredLegs.length && selectedLegs.every((leg) => checkedByOutcome.has(leg.outcome_id)),
  }
}

function quoteTargetIsValid(target) {
  return target?.status === 'valid' && targetHasCanonicalPack(target)
}

// Everything the builder derives from the sportsbook check of the selected
// legs and target regulations: the query itself, what the preview and the
// status panel show, and what save and publish trust.
export function usePrepackQuoteTargets({
  slug,
  canConfigure,
  sourceRegulation,
  targetRegulations,
  previewRegulation,
  selectedLegs,
  canonicalLegs,
  minLegs,
}) {
  const quoteIdentity = useMemo(
    () =>
      JSON.stringify({
        source: sourceRegulation,
        targets: [...targetRegulations].sort(),
        legs: canonicalLegs,
      }),
    [canonicalLegs, sourceRegulation, targetRegulations],
  )

  const quoteEnabled = Boolean(slug && canConfigure && selectedLegs.length > 0 && sourceRegulation && targetRegulations.length > 0)
  const quoteTargetsQuery = useQuery({
    queryKey: quoteTargetsQueryKey(slug, quoteIdentity),
    queryFn: ({ signal }) =>
      prepacksApi
        .quoteTargets(
          slug,
          {
            regulation: sourceRegulation,
            legs: canonicalLegs,
            regulations: targetRegulations,
          },
          { signal },
        )
        // Which legs this check answered, for the preview of the next edit.
        .then((response) => ({ ...response, answeredLegs: canonicalLegs })),
    enabled: quoteEnabled,
    retry: false,
    staleTime: 15000,
    refetchInterval: 30000,
    // Only the preview reads the previous combination's check while a new
    // one runs; everything else reads the current combination (currentQuote).
    placeholderData: (previousData, previousQuery) => (
      previousQuery && editsSameSelection(quoteIdentityOfKey(previousQuery.queryKey), quoteIdentity) ? previousData : undefined
    ),
  })

  // The last completed check for these legs and targets. It survives the 30 s
  // background re-check and a failed re-check, and feeds the status panel and
  // the rejection verdicts. Legs and targets are in the query key, so a
  // combination not checked yet has none; one checked in the last few minutes
  // shows its cached check while it is re-checked.
  const currentQuote = quoteTargetsQuery.isPlaceholderData ? undefined : quoteTargetsQuery.data
  const shownTargetMap = useMemo(() => targetsByRegulation(currentQuote), [currentQuote])
  // While a new leg or regulation is checked, the preview builds on the
  // previous combination's check so it updates in place instead of starting
  // over: it shows the selected legs at once and waits only for the price.
  // Nothing is saved or published from it.
  const updatingPreview = quoteEnabled && quoteTargetsQuery.isPlaceholderData
  const previousTargetMap = useMemo(
    () => targetsByRegulation(updatingPreview ? quoteTargetsQuery.data : undefined),
    [quoteTargetsQuery.data, updatingPreview],
  )
  // What save and publish trust. A failed or in-flight refetch must not keep
  // presenting the previous provider quote as current.
  const gateTargetMap = useMemo(
    () => (quoteTargetsQuery.isFetching || quoteTargetsQuery.isError ? new Map() : shownTargetMap),
    [quoteTargetsQuery.isError, quoteTargetsQuery.isFetching, shownTargetMap],
  )
  const hasCheckedQuote = currentQuote != null
  const recheckingQuote = hasCheckedQuote && quoteTargetsQuery.isFetching
  const recheckFailed = hasCheckedQuote && quoteTargetsQuery.isError && !quoteTargetsQuery.isFetching

  // Verdicts and prices belong to the current combination only.
  const previewsTarget = targetRegulations.includes(previewRegulation)
  const checkedPreviewTarget = previewsTarget ? shownTargetMap.get(previewRegulation) : null
  const previewTarget = updatingPreview ? (previewsTarget ? previousTargetMap.get(previewRegulation) : null) : checkedPreviewTarget
  const previewVerdict = updatingPreview ? null : previewTarget?.status
  // The legs to preview while a leg edit is being checked; null otherwise.
  const answeredLegs = quoteTargetsQuery.data?.answeredLegs
  const pendingPreviewLegs = useMemo(
    () => (updatingPreview && targetHasCanonicalPack(previewTarget)
      ? legsWhileChecking(answeredLegs, previewTarget.pack.legs, canonicalLegs)
      : null),
    [answeredLegs, canonicalLegs, previewTarget, updatingPreview],
  )

  // The last verdict stays up through a re-check. A stale rejection can only
  // block publishing, never allow it.
  const targetStatuses = useMemo(
    () => targetRegulations.map((regulation) => shownTargetMap.get(regulation)),
    [shownTargetMap, targetRegulations],
  )
  const quoteSettled = Boolean(!quoteTargetsQuery.isLoading && !quoteTargetsQuery.isFetching && !quoteTargetsQuery.isError)
  const hasRejectedTargets = useMemo(
    () => targetStatuses.some((target) => target?.status === 'invalid'),
    [targetStatuses],
  )
  const hasRejectedCombination = useMemo(
    () =>
      targetStatuses.some(
        (target) => target?.status === 'invalid' && target?.error?.code !== 'insufficient_legs',
      ),
    [targetStatuses],
  )
  // While the check itself failed, the error box explains the state; a
  // rejection alert or footer hint would contradict it.
  const showRejectedCombination = hasRejectedCombination && !quoteTargetsQuery.isError
  const hasUnresolved = useMemo(
    () =>
      selectedLegs.length > 0 &&
      (!quoteSettled ||
        targetRegulations.some((regulation) => !targetHasCanonicalPack(gateTargetMap.get(regulation)))),
    [gateTargetMap, quoteSettled, selectedLegs.length, targetRegulations],
  )
  // A fully hydrated target is safe to save as a draft even when its price
  // is rejected or temporarily unavailable, but never to publish.
  const draftReady = Boolean(
    selectedLegs.length > 0 &&
      targetRegulations.length > 0 &&
      quoteSettled &&
      targetRegulations.every((regulation) => targetHasCanonicalPack(gateTargetMap.get(regulation))),
  )
  const quoteReady = Boolean(
    selectedLegs.length >= minLegs &&
      targetRegulations.length > 0 &&
      quoteSettled &&
      targetRegulations.every((regulation) => quoteTargetIsValid(gateTargetMap.get(regulation))),
  )

  return {
    quoteTargetsQuery,
    shownTargetMap,
    gateTargetMap,
    hasCheckedQuote,
    recheckingQuote,
    recheckFailed,
    updatingPreview,
    checkedPreviewTarget,
    previewTarget,
    pendingPreviewLegs,
    previewVerdict,
    hasRejectedTargets,
    hasRejectedCombination,
    showRejectedCombination,
    hasUnresolved,
    draftReady,
    quoteReady,
  }
}
